import { useRef } from 'react';

import {
	type RemixCommand,
	useRemixCommands,
} from '@atlassian/malleable-ui-components/state/machine';

type RemixReadyCommand = Extract<RemixCommand, { type: 'RemixReady' }>;
type RemixErroredCommand = Extract<RemixCommand, { type: 'RemixErrored' }>;
type RemixAbortedCommand = Extract<RemixCommand, { type: 'RemixAborted' }>;
type RemixGenerationEndedCommand = Extract<RemixCommand, { type: 'RemixGenerationEnded' }>;
type RendererRemixEffectCommand =
	| RemixReadyCommand
	| RemixErroredCommand
	| RemixAbortedCommand
	| RemixGenerationEndedCommand;
type RendererRemixEffectClaim =
	| Exclude<RendererRemixEffectCommand['type'], 'RemixGenerationEnded'>
	| `RemixGenerationEnded:${RemixGenerationEndedCommand['reason']}`;

export type RendererRemixCommandHandlers = {
	onReady: (command: RemixReadyCommand) => void;
	onErrored: (command: RemixErroredCommand) => void;
	onAborted: (command: RemixAbortedCommand) => void;
	onGenerationEnded: (command: RemixGenerationEndedCommand) => void;
};

/**
 * Owns renderer product effects for one admitted flow.
 *
 * The default dispatcher fans every command out process-wide, so ownership is
 * established by the exact admitted remix ID. Command-type claims make repeated
 * delivery harmless without retaining product state after this component unmounts.
 */
export const useRendererRemixCommands = (
	isAdmittedRemixId: (remixId: string) => boolean,
	{ onReady, onErrored, onAborted, onGenerationEnded }: RendererRemixCommandHandlers,
): void => {
	const handledRef = useRef<{
		remixId: string | undefined;
		commands: Set<RendererRemixEffectClaim>;
	}>({ remixId: undefined, commands: new Set() });

	useRemixCommands((command) => {
		if (!isAdmittedRemixId(command.remixId)) {
			return;
		}

		if (handledRef.current.remixId !== command.remixId) {
			handledRef.current = { remixId: command.remixId, commands: new Set() };
		}

		let claim: RendererRemixEffectClaim;
		switch (command.type) {
			case 'RemixReady':
			case 'RemixErrored':
			case 'RemixAborted':
				claim = command.type;
				break;
			case 'RemixGenerationEnded':
				claim = `${command.type}:${command.reason}`;
				break;
			default:
				return;
		}
		if (handledRef.current.commands.has(claim)) {
			return;
		}
		handledRef.current.commands.add(claim);

		switch (command.type) {
			case 'RemixReady':
				onReady(command);
				break;
			case 'RemixErrored':
				onErrored(command);
				break;
			case 'RemixAborted':
				onAborted(command);
				break;
			case 'RemixGenerationEnded':
				onGenerationEnded(command);
				break;
		}
	});
};
